// Normal sign-in for the standalone console: OpenID Connect authorization code with PKCE against the realm `lcc`
// (the realm's public client for the console; MFA is enforced by the realm for staff). Tokens stay in memory; only
// the PKCE verifier and state wait in sessionStorage during the redirect. When the identity service is unreachable
// the console offers the FER local sign-in instead (FireApp).
import { tokenClaims } from "../auth/localAuth";
import type { Principal } from "../types";

export interface OidcSettings {
  issuer: string;
  clientId: string;
  redirectUri: string;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
}

const PENDING = "lcc.fer.console.oidc-pending";

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomString(bytes = 32): string {
  const b = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(b);
  return b64url(b);
}

/** The PKCE S256 challenge of a verifier. */
export async function challengeOf(verifier: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

export function authorizeUrl(s: OidcSettings, state: string, challenge: string): string {
  const q = new URLSearchParams({
    response_type: "code",
    client_id: s.clientId,
    redirect_uri: s.redirectUri,
    scope: "openid",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${s.issuer}/protocol/openid-connect/auth?${q}`;
}

/** Start a sign-in: remember the verifier and state for this tab, then go to the realm's sign-in page. */
export async function startSignIn(s: OidcSettings, go: (url: string) => void = (u) => globalThis.location.assign(u)): Promise<void> {
  const verifier = randomString();
  const state = randomString(16);
  try {
    sessionStorage.setItem(PENDING, JSON.stringify({ verifier, state }));
  } catch {
    // No session storage: the callback cannot be checked, so no sign-in completes (safe).
  }
  go(authorizeUrl(s, state, await challengeOf(verifier)));
}

async function tokenRequest(s: OidcSettings, form: Record<string, string>, fetchImpl: typeof fetch): Promise<Tokens | null> {
  try {
    const res = await fetchImpl(`${s.issuer}/protocol/openid-connect/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: s.clientId, ...form }).toString(),
    });
    if (!res.ok) return null;
    const t = (await res.json()) as { access_token?: unknown; refresh_token?: unknown; expires_in?: unknown };
    if (typeof t.access_token !== "string") return null;
    const ttl = typeof t.expires_in === "number" ? t.expires_in : 300;
    return { accessToken: t.access_token, refreshToken: typeof t.refresh_token === "string" ? t.refresh_token : null, expiresAt: Date.now() + ttl * 1000 };
  } catch {
    return null;
  }
}

/**
 * Finish a sign-in on the redirect back: the state must match the one this tab stored (else nothing happens), then the
 * code is exchanged with the verifier. Answers null when this page load is not a sign-in callback or it failed.
 */
export async function completeSignIn(s: OidcSettings, pageUrl: string, fetchImpl: typeof fetch = fetch): Promise<Tokens | null> {
  const u = new URL(pageUrl);
  const code = u.searchParams.get("code");
  const state = u.searchParams.get("state");
  if (!code || !state) return null;
  let pending: { verifier?: string; state?: string } = {};
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING) ?? "{}") as typeof pending;
    sessionStorage.removeItem(PENDING);
  } catch {
    return null;
  }
  if (!pending.verifier || pending.state !== state) return null;
  return tokenRequest(s, { grant_type: "authorization_code", code, redirect_uri: s.redirectUri, code_verifier: pending.verifier }, fetchImpl);
}

export function refresh(s: OidcSettings, refreshToken: string, fetchImpl: typeof fetch = fetch): Promise<Tokens | null> {
  return tokenRequest(s, { grant_type: "refresh_token", refresh_token: refreshToken }, fetchImpl);
}

/** The principal of a realm access token (display only; FER verifies every request). Staff roles count only on staff accounts. */
export function principalFromAccessToken(token: string): Principal | null {
  const c = tokenClaims(token);
  const party = typeof c.party_id === "string" ? c.party_id : typeof c.sub === "string" ? c.sub : null;
  if (!party) return null;
  const realm = (c.realm_access as { roles?: unknown } | undefined)?.roles;
  const roles = Array.isArray(realm) ? realm.filter((r): r is string => typeof r === "string") : [];
  const wards = Array.isArray(c.ward_ids) ? c.ward_ids.filter((w): w is string => typeof w === "string") : typeof c.ward_ids === "string" ? [c.ward_ids] : [];
  const staff = c.account_type === "staff";
  return {
    roles: staff ? roles : roles.filter((r) => !r.startsWith("fer:") && !r.startsWith("hub:")),
    wardIds: wards,
    partyId: party,
    displayName: typeof c.name === "string" ? c.name : null,
    auth: "realm",
  };
}
